import moment from "moment";

import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { TableDataSourceItem } from "@/Common/Models/UI/Tables/ITableDataSourceItem";
import { ValueSetType } from "../Constants/ValueSetType";
import { ConditionModel } from "../Models/ConditionModel";
import { QuebecVaccinationRegistryHelper } from "../QuebecVaccinationRegistryHelper";
import { BaseImmunizationTable, BaseImmunizationTableProps } from "./BaseImmunizationTable";

interface ImmunityProofTableProps extends BaseImmunizationTableProps<ConditionModel> {
    onActionClick: (item: ConditionModel, isReadOnly: boolean) => void;
}

class ImmunityProofTable extends BaseImmunizationTable<ImmunityProofTableProps, ConditionModel> {
    protected title(): string {
        return cacheStore.GetString("QuebecVaccinationRegistry_ProofOfImmunityLabel");
    }

    protected tableId(): string {
        return "immunity-proof-table";
    }

    private labels = {
        ImmunizationAgent: "QuebecVaccinationRegistry_AntigenLabel",
        Motive: "QuebecVaccinationRegistry_MotiveLabel",
        AttestationSource: "QuebecVaccinationRegistry_SourceOfAttestationLabel",
        ApplicableFrom: "QuebecVaccinationRegistry_ApplicableFromLabel",
        ApplicableTo: "QuebecVaccinationRegistry_UntilLabel",
        Action: "ActionLabel"
    };

    private fieldNames = {
        ImmunizationAgent: "ImmunizationAgent",
        Motive: "Motive",
        AttestationSource: "AttestationSource",
        ApplicableFrom: "ApplicableFrom",
        ApplicableTo: "ApplicableTo",
        Action: "Action"
    };

    protected defaultTableOptions(): Immutable.List<MyleTableColumnOptions> {
        return Immutable.List([
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.ImmunizationAgent),
                FieldName: this.fieldNames.ImmunizationAgent,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Motive),
                FieldName: this.fieldNames.Motive,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.AttestationSource),
                FieldName: this.fieldNames.AttestationSource,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.ApplicableFrom),
                FieldName: this.fieldNames.ApplicableFrom,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.ApplicableTo),
                FieldName: this.fieldNames.ApplicableTo,
                WidthPercentageDefault: 16
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString(this.labels.Action),
                FieldName: this.fieldNames.Action,
                WidthPercentageDefault: 16
            })
        ]);
    }

    protected getTableColumns(tableOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<TableDataSourceItem<ConditionModel>>(
            tableOptions,
            column => {
                switch (column.FieldName) {
                    case this.fieldNames.ImmunizationAgent:
                        return {
                            renderCell: item => (
                                <div>
                                    {item.Model.Agent?.Code
                                        ? QuebecVaccinationRegistryHelper.translateValueSetString(item.Model.Agent, ValueSetType.Agent)
                                        : QuebecVaccinationRegistryHelper.translateValueSetString(item.Model.Antigen, ValueSetType.Antigen)
                                    }
                                </div>
                            )
                        };

                    case this.fieldNames.Motive:
                        return {
                            renderCell: item => (
                                <div>
                                    {QuebecVaccinationRegistryHelper.translateValueSetString(
                                        item.Model.Reason,
                                        "ProofOfImmunityMotive"
                                    )}
                                </div>
                            )
                        };

                    case this.fieldNames.AttestationSource:
                        return {
                            renderCell: item => (
                                <div>
                                    {item.Model.Evidence
                                        .map(i => QuebecVaccinationRegistryHelper.translateValueSetString(i, ValueSetType.EvidenceSource))
                                        .join("\n")
                                    }
                                </div>
                            )
                        };

                    case this.fieldNames.ApplicableFrom:
                        return {
                            renderCell: item => <div>{this.formatDate(item.Model.DatePeriod.Start)}</div>
                        };

                    case this.fieldNames.ApplicableTo:
                        return {
                            renderCell: item => <div>{this.formatDate(item.Model.DatePeriod.End)}</div>
                        };

                    case this.fieldNames.Action:
                        return {
                            renderCell: item => {
                                const isReadonly = this.props.isReadOnly || moment(item.Model.DatePeriod.End).isBefore(moment().startOf("day"));
                                return (
                                    <div>
                                        <a onClick={() => this.props.onActionClick(item.Model, isReadonly)}>
                                            {cacheStore.GetString(isReadonly ? "ViewLabel" : "ModifyLabel")}
                                        </a>
                                    </div>
                                );
                            }
                        };
                }
            },
            false
        );
    }
}

export { ImmunityProofTable };
